import {
  collection,
  deleteField,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebase";
import { api } from "../lib/api";
import { seedMilestonesFor } from "./pricing";

const projectsCol = collection(db, "projects");

// Fire-and-forget push to the project's account members via the backend.
// Fails silently (e.g. non-admin caller, API down) — never blocks the write.
const notifyOwner = async (projectId, event, detail) => {
  try {
    const snap = await getDoc(doc(db, "projects", projectId));
    if (!snap.exists()) return;
    const p = snap.data();
    await api.post("/project-event", {
      accountId: p.accountId,
      ownerUid: p.ownerUid || p.primaryContactUid, // legacy fallback
      event,
      projectTitle: p.title,
      detail,
    });
  } catch (err) {
    console.warn("[notify]", err?.message);
  }
};

export const subscribeAllProjects = (cb) =>
  onSnapshot(query(projectsCol, orderBy("updatedAt", "desc")), (snap) =>
    cb(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
  );

// Every project belonging to an account — the client dashboard's primary query.
// All members of the account see the same projects (visibility is account-wide,
// gated by role only for writes).
export const subscribeAccountProjects = (accountId, cb) => {
  if (!accountId) {
    cb([]);
    return () => {};
  }
  return onSnapshot(
    query(projectsCol, where("accountId", "==", accountId), orderBy("updatedAt", "desc")),
    (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
  );
};

export const subscribeMilestones = (projectId, cb) =>
  onSnapshot(
    query(collection(db, "projects", projectId, "milestones"), orderBy("order", "asc")),
    (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
  );

export const subscribeUpdates = (projectId, cb) =>
  onSnapshot(
    query(collection(db, "projects", projectId, "updates"), orderBy("createdAt", "desc")),
    (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
  );

export const subscribeMessages = (projectId, cb) =>
  onSnapshot(
    query(collection(db, "projects", projectId, "messages"), orderBy("createdAt", "asc")),
    (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
  );

export const subscribeClients = (cb) =>
  onSnapshot(query(collection(db, "users"), where("isClient", "==", true)), (snap) =>
    cb(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
  );

export const subscribeAllUsers = (cb) =>
  onSnapshot(collection(db, "users"), (snap) =>
    cb(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
  );

// Manual project creation (admin path that doesn't go through a request).
// Atomically writes the project + seeded milestones under an ACCOUNT. Account
// membership (and each member's isClient flag) is managed by the accounts API,
// so this no longer touches user docs — it just stamps the owning accountId.
export const createProjectManual = async ({
  accountId,
  title,
  summary,
  type,
  budgetUsd,
  paymentPlan,
  primaryContactUid,
}) => {
  if (!accountId) throw new Error("accountId required");
  if (!title) throw new Error("title required");

  const projectRef = doc(projectsCol);
  const milestones = seedMilestonesFor({ paymentPlan, budgetUsd });

  const batch = writeBatch(db);
  batch.set(projectRef, {
    accountId,
    primaryContactUid: primaryContactUid || null,
    requestId: null,
    title,
    summary: summary || "",
    type: type || null,
    status: "discovery",
    budgetUsd: Number(budgetUsd) || 0,
    paymentPlan,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  milestones.forEach((m) => {
    const mRef = doc(collection(projectRef, "milestones"));
    batch.set(mRef, {
      label: m.label,
      pct: m.pct,
      amount: m.amount,
      dueAt: null,
      status: "pending",
      paidAt: null,
      order: m.order,
    });
  });

  await batch.commit();
  return projectRef.id;
};

export const updateProject = (id, patch) =>
  updateDoc(doc(db, "projects", id), { ...patch, updatedAt: serverTimestamp() });

export const archiveProject = (id) =>
  updateDoc(doc(db, "projects", id), {
    archived: true,
    archivedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

export const unarchiveProject = (id) =>
  updateDoc(doc(db, "projects", id), {
    archived: false,
    archivedAt: deleteField(),
    updatedAt: serverTimestamp(),
  });

export const deleteProject = async (id) => {
  const projectRef = doc(db, "projects", id);
  const subcollections = ["milestones", "updates", "messages"];
  const subSnaps = await Promise.all(
    subcollections.map((name) => getDocs(collection(projectRef, name)))
  );
  const batch = writeBatch(db);
  subSnaps.forEach((snap) => snap.docs.forEach((d) => batch.delete(d.ref)));
  batch.delete(projectRef);
  await batch.commit();
};

export const setMilestoneStatus = async (projectId, milestoneId, status) => {
  const mRef = doc(db, "projects", projectId, "milestones", milestoneId);
  await updateDoc(mRef, {
    status,
    paidAt: status === "paid" ? serverTimestamp() : null,
  });
  if (status === "invoiced" || status === "paid") {
    const m = await getDoc(mRef);
    notifyOwner(projectId, `milestone_${status}`, m.get("label") || "Milestone");
  }
};

export const addUpdate = async (projectId, { phase, title, body, author }) => {
  const ref = doc(collection(db, "projects", projectId, "updates"));
  const batch = writeBatch(db);
  batch.set(ref, {
    phase: phase || "",
    title: title || "",
    body: body || "",
    authorUid: author.uid,
    authorName: author.name || author.email || "Studio",
    createdAt: serverTimestamp(),
  });
  batch.update(doc(db, "projects", projectId), { updatedAt: serverTimestamp() });
  await batch.commit();
  notifyOwner(projectId, "update", title);
  return ref.id;
};

export const sendMessage = async (projectId, { body, author }) => {
  const ref = doc(collection(db, "projects", projectId, "messages"));
  const batch = writeBatch(db);
  batch.set(ref, {
    authorUid: author.uid,
    authorName: author.name || author.email || "User",
    body,
    createdAt: serverTimestamp(),
    readBy: [author.uid],
  });
  batch.update(doc(db, "projects", projectId), {
    lastMessageAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  await batch.commit();
  notifyOwner(projectId, "message", (body || "").slice(0, 60));
  return ref.id;
};

// Read-once helper for admin lists that want to show milestone roll-ups.
export const fetchMilestonesOnce = async (projectId) => {
  const snap = await getDocs(
    query(collection(db, "projects", projectId, "milestones"), orderBy("order", "asc"))
  );
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};
